import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton
} from '@mui/material';
import { Close, Delete } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../../../../app/hooks';
import { closeViewNoteDialog } from '../../../state';
import { useNotesManagement } from '../../../hooks/useNotesManagement';

export const ViewNoteDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showViewNoteDialog, selectedNote } = useAppSelector(state => state.ticket.ui);
  const { deleteNote, canDeleteNote } = useNotesManagement();

  const handleClose = () => {
    dispatch(closeViewNoteDialog());
  };

  const handleDelete = () => {
    if (!selectedNote) return;
    
    if (window.confirm('Are you sure you want to delete this note?')) {
      deleteNote(selectedNote.id);
      handleClose();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!selectedNote) return null;

  return (
    <Dialog
      open={showViewNoteDialog}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: 600,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Typography variant="h6">View Note</Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 4 }}>
        <Box>
          {/* Note metadata */}
          <Box display="flex" gap={2} alignItems="center" sx={{ mb:4  }}>
            <Chip 
              label={selectedNote.author} 
              size="small" 
              variant="outlined"
              sx={{ fontWeight: 500, mt: 2 }}
            />
            <Typography variant="caption" color="textSecondary" mt={2}>
              Created: {formatDate(selectedNote.createdDate)}
            </Typography>
          </Box>
          
          {/* Note content */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5,
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 1.5
            }}
          >
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                color: '#1e293b'
              }}
            >
              {selectedNote.comment}
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 2.5, 
        borderTop: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc',
        justifyContent: 'space-between'
      }}>
        <Box>
          {canDeleteNote(selectedNote) && (
            <Button
              startIcon={<Delete />}
              onClick={handleDelete}
              color="error"
              variant="outlined"
              size="small"
            >
              Delete Note
            </Button>
          )}
        </Box>
        
        <Button 
          onClick={handleClose}
          variant="contained"
          sx={{
            backgroundColor: '#162346',
            '&:hover': { backgroundColor: '#1a2a52' }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
